import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  AlertTriangle,
  Calendar,
  Clock,
  AlertCircle,
  X,
  Check,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';

/* ==========================================================================
   NotificationBell — header icon with dropdown for unread notifications.
   Polls /api/notifications/unread-count every 30 seconds.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export type NotificationType =
  | 'laser_inactive'
  | 'checkup_due'
  | 'package_expiring'
  | 'service_missing';

export interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  body: string;
  clientId: number | null;
  entityId: number | null;
  readAt: string | null;
  createdAt: string;
}

interface UnreadCountResponse {
  unreadCount: number;
}

interface NotificationsResponse {
  data: Notification[];
  unreadCount: number;
}

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const POLL_INTERVAL_MS = 30_000;
const MAX_DROPDOWN_ITEMS = 20;

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

export function timeAgo(date: string | Date): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'Ora';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min fa`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h fa`;
  const days = Math.floor(seconds / 86400);
  if (days === 1) return 'Ieri';
  if (days < 30) return `${days}g fa`;
  return `${Math.floor(days / 30)}m fa`;
}

const TYPE_CONFIG: Record<
  NotificationType,
  { Icon: typeof Bell; iconClass: string; bgClass: string }
> = {
  laser_inactive: {
    Icon: AlertTriangle,
    iconClass: 'text-amber-500',
    bgClass: 'bg-amber-50',
  },
  checkup_due: {
    Icon: Calendar,
    iconClass: 'text-status-info',
    bgClass: 'bg-status-info-light',
  },
  package_expiring: {
    Icon: Clock,
    iconClass: 'text-accent-primary',
    bgClass: 'bg-accent-primary-light',
  },
  service_missing: {
    Icon: AlertCircle,
    iconClass: 'text-status-negative',
    bgClass: 'bg-status-negative-light',
  },
};

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function NotificationBell() {
  const navigate = useNavigate();

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // ------------------------------------------------------------------
  // Poll unread count
  // ------------------------------------------------------------------

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await api.get<UnreadCountResponse>(
        '/notifications/unread-count',
      );
      setUnreadCount(data?.unreadCount ?? 0);
    } catch {
      // Silently ignore polling errors
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // ------------------------------------------------------------------
  // Load notification list when dropdown opens
  // ------------------------------------------------------------------

  const loadNotifications = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const data = await api.get<NotificationsResponse>('/notifications');
      setNotifications((data?.data ?? []).slice(0, MAX_DROPDOWN_ITEMS));
      setUnreadCount(data?.unreadCount ?? 0);
    } catch (err) {
      if (err instanceof ApiError) {
        // Keep existing list on error
      }
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  // ------------------------------------------------------------------
  // Close on outside click
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;

    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // ------------------------------------------------------------------
  // Mark single notification read
  // ------------------------------------------------------------------

  const handleMarkRead = useCallback(
    async (notification: Notification) => {
      if (notification.readAt) return;
      try {
        await api.post(`/notifications/${notification.id}/read`);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id
              ? { ...n, readAt: new Date().toISOString() }
              : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // Ignore
      }
    },
    [],
  );

  // ------------------------------------------------------------------
  // Mark all read
  // ------------------------------------------------------------------

  const handleMarkAllRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          readAt: n.readAt ?? new Date().toISOString(),
        })),
      );
      setUnreadCount(0);
    } catch {
      // Ignore
    }
  }, []);

  // ------------------------------------------------------------------
  // Navigate to entity
  // ------------------------------------------------------------------

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      await handleMarkRead(notification);
      if (notification.clientId) {
        navigate(`/clients/${notification.clientId}`);
      }
      setIsOpen(false);
    },
    [handleMarkRead, navigate],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'relative flex items-center justify-center',
          'h-[var(--touch-min)] w-[var(--touch-min)]',
          'rounded-[var(--radius-md)]',
          'text-text-primary',
          'transition-colors duration-[var(--duration-fast)]',
          'hover:bg-surface-sunken active:bg-border-default',
          'cursor-pointer',
          isOpen && 'bg-surface-sunken',
        )}
        aria-label={
          unreadCount > 0
            ? `Notifiche: ${unreadCount} non lette`
            : 'Notifiche'
        }
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell className="h-5 w-5" strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute top-1.5 right-1.5',
              'flex h-4 min-w-4 items-center justify-center',
              'rounded-full bg-accent-primary',
              'text-[10px] font-bold text-white px-0.5',
              'ring-2 ring-surface-base',
            )}
            aria-hidden
          >
            {displayCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(
            'absolute right-0 top-full mt-2 z-[var(--z-dropdown)]',
            'w-80 max-h-[480px]',
            'bg-surface-card rounded-[var(--radius-lg)]',
            'border border-border-default shadow-lg',
            'flex flex-col overflow-hidden',
            'fade-in',
          )}
          role="dialog"
          aria-label="Notifiche"
        >
          {/* Dropdown header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-default shrink-0">
            <h3 className="font-[family-name:var(--font-heading)] text-sm font-bold text-text-primary">
              Notifiche
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-accent-primary text-[11px] font-bold text-white px-1">
                  {displayCount}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className={cn(
                    'flex items-center gap-1 px-2 py-1',
                    'rounded-[var(--radius-sm)]',
                    'text-xs font-medium text-text-muted',
                    'hover:text-text-primary hover:bg-surface-sunken',
                    'transition-colors cursor-pointer',
                  )}
                  title="Segna tutto come letto"
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={2} />
                  Segna tutto
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className={cn(
                  'flex items-center justify-center',
                  'h-7 w-7 rounded-[var(--radius-sm)]',
                  'text-text-muted hover:text-text-primary',
                  'hover:bg-surface-sunken',
                  'transition-colors cursor-pointer',
                )}
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto flex-1">
            {isLoadingList && notifications.length === 0 ? (
              <div className="py-8 text-center">
                <div className="inline-block h-5 w-5 rounded-full border-2 border-accent-primary border-t-transparent animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <Bell className="mx-auto h-8 w-8 text-text-muted mb-2" strokeWidth={1} />
                <p className="text-sm text-text-muted">Nessuna notifica</p>
              </div>
            ) : (
              <ul>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={() => handleNotificationClick(notification)}
                    onMarkRead={() => handleMarkRead(notification)}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Footer: Vedi tutte */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-border-default shrink-0">
              <button
                onClick={() => {
                  navigate('/notifications');
                  setIsOpen(false);
                }}
                className={cn(
                  'w-full text-center text-sm font-medium text-accent-primary',
                  'hover:text-accent-primary-hover transition-colors cursor-pointer',
                  'py-1',
                )}
              >
                Vedi tutte le notifiche
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   NotificationItem
   -------------------------------------------------------------------------- */

function NotificationItem({
  notification,
  onClick,
  onMarkRead,
}: {
  notification: Notification;
  onClick: () => void;
  onMarkRead: () => void;
}) {
  const config = TYPE_CONFIG[notification.type] ?? TYPE_CONFIG.checkup_due;
  const { Icon, iconClass, bgClass } = config;
  const isUnread = !notification.readAt;

  return (
    <li>
      <div
        className={cn(
          'flex items-start gap-3 px-4 py-3',
          'border-b border-border-default last:border-b-0',
          'transition-colors duration-[var(--duration-fast)]',
          isUnread ? 'bg-surface-base' : 'bg-surface-card',
          'hover:bg-surface-sunken',
          notification.clientId && 'cursor-pointer',
        )}
        onClick={notification.clientId ? onClick : undefined}
        role={notification.clientId ? 'button' : undefined}
      >
        {/* Icon */}
        <div
          className={cn(
            'flex items-center justify-center h-8 w-8 rounded-full shrink-0 mt-0.5',
            bgClass,
          )}
        >
          <Icon className={cn('h-4 w-4', iconClass)} strokeWidth={1.5} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm leading-snug',
              'font-[family-name:var(--font-heading)]',
              isUnread
                ? 'font-semibold text-text-primary'
                : 'font-medium text-text-secondary',
            )}
          >
            {notification.title}
          </p>
          <p className="text-xs text-text-muted mt-0.5 line-clamp-2 leading-relaxed">
            {notification.body}
          </p>
          <p className="text-xs text-text-muted mt-1">
            {timeAgo(notification.createdAt)}
          </p>
        </div>

        {/* Unread dot + mark read button */}
        <div className="shrink-0 flex flex-col items-end gap-1 mt-0.5">
          {isUnread && (
            <>
              <span
                className="h-2 w-2 rounded-full bg-accent-primary"
                aria-hidden
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkRead();
                }}
                className={cn(
                  'flex items-center justify-center h-6 w-6',
                  'rounded-[var(--radius-sm)]',
                  'text-text-muted hover:text-text-primary',
                  'hover:bg-surface-sunken',
                  'transition-colors cursor-pointer',
                )}
                aria-label="Segna come letta"
                title="Segna come letta"
              >
                <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}
